import { useState } from 'react';
import { AlertCircle, ArrowRight, LogIn } from 'lucide-react';
import kodiakDarkLogo from '../../assets/images/kodiac-dark-640w.png';

interface LoginProps {
  onLogin: (email: string, password: string) => Promise<void>;
  error?: string | null;
}

export function Login({ onLogin, error }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onLogin(email, password);
    } catch (err) {
      // Error is handled by parent component
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1628] flex">
      {/* Left Side - Platform Updates */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-2">
            <img src={kodiakDarkLogo} alt="Kodiac" className="h-8" />
          </div>
          <p className="text-gray-400 text-sm">AI Visibility & Representation Infrastructure</p>
        </div>

        <div className="flex-1">
          <div className="mb-8">
            <p className="text-xs font-semibold text-t1-primary uppercase tracking-wide mb-3">PLATFORM UPDATES & NEWS</p>
            <h1 className="text-3xl font-semibold text-white mb-1">What's new in Kodiac —</h1>
            <h2 className="text-3xl font-semibold text-t1-primary">updates, fixes & features</h2>
          </div>

          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="text-gray-400">1,247 users active</span>
            </div>
            <span className="px-2 py-1 bg-t1-primary text-white text-xs font-semibold rounded">3 unread</span>
          </div>

          <div className="space-y-4">
            {/* Update Cards */}
            <div className="border-t border-blue-900 pt-4">
              <div className="flex items-start justify-between mb-3">
                <span className="px-2 py-1 bg-blue-600 text-white text-xs font-semibold rounded uppercase">New Feature</span>
                <span className="text-xs text-gray-500">28 Apr 2026</span>
              </div>
              <h3 className="text-white font-semibold mb-2">Kodiac Agent now supports custom guardrail rules</h3>
              <p className="text-sm text-gray-400 mb-3">
                You can now define fully custom guardrail rules in plain language from the Agent configuration panel. Rules are applied in real time to all Brand Agent interactions. Custom rules support keyword matching, topic detection, and intent-based triggers.
              </p>
              <a href="#" className="text-sm text-t1-primary hover:underline inline-flex items-center gap-1">
                Read release notes <ArrowRight className="w-3 h-3" />
              </a>
            </div>

            <div className="border-t border-amber-900 pt-4">
              <div className="flex items-start justify-between mb-3">
                <span className="px-2 py-1 bg-amber-600 text-white text-xs font-semibold rounded uppercase">Scheduled Maintenance</span>
                <span className="text-xs text-gray-500">30 Apr 2026</span>
              </div>
              <h3 className="text-white font-semibold mb-2">Planned downtime: 30 April 02:00-04:00 UTC</h3>
              <p className="text-sm text-gray-400">
                We are performing infrastructure upgrades to improve Audit scan speed and Content sync reliability. The platform will be unavailable for up to 2 hours. All scheduled scans will be deferred and run automatically when the platform returns.
              </p>
            </div>

            <div className="border-t border-emerald-900 pt-4">
              <div className="flex items-start justify-between mb-3">
                <span className="px-2 py-1 bg-emerald-600 text-white text-xs font-semibold rounded uppercase">Kodiac Content</span>
                <span className="text-xs text-gray-500">24 Apr 2026</span>
              </div>
              <h3 className="text-white font-semibold mb-2">Salesforce and HubSpot connectors: delta sync now live</h3>
              <p className="text-sm text-gray-400 mb-3">
                Content connectors for Salesforce and HubSpot now support delta sync — only changed records are updated on each cycle, reducing sync time by up to 80% for large estates. This is enabled by default for all Growth and Enterprise plans.
              </p>
              <a href="#" className="text-sm text-t1-primary hover:underline inline-flex items-center gap-1">
                View connector docs <ArrowRight className="w-3 h-3" />
              </a>
            </div>

            <div className="border-t border-purple-900 pt-4">
              <div className="flex items-start justify-between mb-3">
                <span className="px-2 py-1 bg-purple-600 text-white text-xs font-semibold rounded uppercase">Platform Insight</span>
                <span className="text-xs text-gray-500">21 Apr 2026</span>
              </div>
              <h3 className="text-white font-semibold mb-2">April benchmark: average AI visibility score now 38/100 across our customer base</h3>
              <p className="text-sm text-gray-400 mb-3">
                Our monthly platform benchmark shows the average score across all Kodiac Audit customers has risen from 34 to 38, driven by customers applying structured data fixes. The top-performing sector remains financial services at 54/100.
              </p>
              <a href="#" className="text-sm text-t1-primary hover:underline inline-flex items-center gap-1">
                See full benchmark <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 bg-[#0A1628] flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-t1-primary/10 rounded-full mb-6">
              <LogIn className="w-8 h-8 text-t1-primary" />
            </div>
            <h1 className="text-2xl font-semibold text-white mb-2">Sign in to your workspace</h1>
            <p className="text-sm text-gray-400">Enter your credentials to access the platform</p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-700 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-300 mb-2">
                    {error.includes('Account setup incomplete') ? 'Account Setup Incomplete' : 'Error'}
                  </p>
                  <div className="text-sm text-red-200 whitespace-pre-line">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Work Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Work email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-[#1a2332] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-t1-primary transition-colors"
                placeholder="sarah.chen@acmecorp.com"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-[#1a2332] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-t1-primary transition-colors"
                placeholder="••••••••"
              />
            </div>

            {/* Keep me signed in & Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={keepSignedIn}
                  onChange={(e) => setKeepSignedIn(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-700 bg-[#1a2332] text-t1-primary focus:ring-t1-primary focus:ring-offset-0"
                />
                <span className="text-sm text-gray-300">Keep me signed in</span>
              </label>
              <a href="#" className="text-sm text-t1-primary hover:underline">
                Forgot password?
              </a>
            </div>

            {/* Sign in button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-t1-primary text-white py-3 rounded-lg font-semibold hover:bg-t1-dark focus:ring-4 focus:ring-t1-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <ArrowRight className="w-5 h-5" />
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          {/* Divider */}
          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 border-t border-gray-700"></div>
            <span className="text-sm text-gray-500">or continue with</span>
            <div className="flex-1 border-t border-gray-700"></div>
          </div>

          {/* SSO Options */}
          <div className="space-y-3">
            <button className="w-full px-4 py-3 bg-[#1a2332] border border-gray-700 rounded-lg text-white hover:bg-[#233044] transition-colors flex items-center justify-center gap-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                <path d="M7 12h10M12 7v10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Continue with SSO
            </button>
            <button className="w-full px-4 py-3 bg-[#1a2332] border border-gray-700 rounded-lg text-white hover:bg-[#233044] transition-colors flex items-center justify-center gap-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
